import React, { ReactNode } from 'react';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { components } from 'react-select';

import pluralize from 'pluralize';
import { Text } from '../../../Text/component';
import {
  MenuListActionBar,
  BulkActionButton,
  DummyCheckbox,
  StyledButton,
  StyledDropdownIcon,
  StyledOption,
  ValueContainerStatus,
  MenuListWrapper,
} from './componentStyles';

export const DropdownIndicator: typeof components.DropdownIndicator = (
  props
) => {
  const { selectProps } = props;
  return (
    <components.DropdownIndicator {...props}>
      {selectProps.menuIsOpen ? (
        <StyledDropdownIcon icon={faCaretUp} />
      ) : (
        <StyledDropdownIcon icon={faCaretDown} />
      )}
    </components.DropdownIndicator>
  );
};

export const Option: typeof components.Option = (props) => {
  const { children, ...passedProps } = props;
  const {
    isSelected,
    isMulti,
    data,
    selectOption,
    selectProps,
    getValue,
  } = passedProps;

  const { maxItemsSelected } = selectProps;
  const value = getValue();
  const selectedCount = Array.isArray(value) ? value.length : 0;

  // Note that retrieving additional components (like Option Content)
  // does work, but it's hard to make it compliant with TS.
  // Hence the type casting below.

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const { OptionContent } = selectProps.components as typeof components & {
    OptionContent: React.FC<any>;
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const isDisabled = !isSelected && selectedCount >= maxItemsSelected;

  return (
    <components.Option {...passedProps} isDisabled={isDisabled}>
      <StyledOption
        onClick={() => {
          if (isSelected && isMulti && !isDisabled) {
            selectOption(data);
          }
        }}
      >
        {isMulti && (
          <DummyCheckbox
            type="checkbox"
            checked={isSelected}
            disabled={isDisabled}
          />
        )}
        {OptionContent ? (
          <OptionContent {...data}>{children}</OptionContent>
        ) : (
          children
        )}
      </StyledOption>
    </components.Option>
  );
};

export const MenuList: typeof components.MenuList = (props) => {
  const { children, isMulti, ...restProps } = props;
  const {
    selectProps: { itemName, onSave, onSelectAll, onClearAll, value },
  } = restProps;

  const selectedCount = Array.isArray(value) ? value.length : 0;
  const pluralItemWord = pluralize(itemName || 'item', selectedCount, true);

  return (
    <MenuListWrapper>
      {isMulti && (
        <MenuListActionBar className="topBar">
          <BulkActionButton onClick={onSelectAll}>Select all</BulkActionButton>
          <span className="separator">|</span>
          <BulkActionButton onClick={onClearAll}>Clear all</BulkActionButton>
        </MenuListActionBar>
      )}
      <components.MenuList isMulti={isMulti} {...restProps}>
        {children}
      </components.MenuList>
      {onSave && (
        <MenuListActionBar className="bottomBar">
          <StyledButton
            variant="platform-primary"
            action={{ kind: 'on_click', value: () => onSave() }}
          >
            Save
          </StyledButton>
          {selectedCount > 0 && (
            <Text size="formBodyText">{pluralItemWord} selected</Text>
          )}
        </MenuListActionBar>
      )}
    </MenuListWrapper>
  );
};

export const ValueContainer: typeof components.ValueContainer = (props) => {
  const {
    children,
    selectProps: { itemName, menuIsOpen },
  } = props;
  let values = (children as ReactNode[])[0];
  const input = (children as ReactNode[])[1];

  if (menuIsOpen) {
    values = null;
  } else if (Array.isArray(values)) {
    const pluralItemWord = pluralize(itemName || 'item', values.length, true);
    values = `${pluralItemWord} selected` as ReactNode;
  }

  return (
    <components.ValueContainer {...props}>
      {values && <ValueContainerStatus>{values}</ValueContainerStatus>}
      {input}
    </components.ValueContainer>
  );
};
